import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import {
  useAllTasksFilterDocument,
  useUpdateAllTasksFilterDocument,
} from 'features/user';
import React, { Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { XCircle } from 'shared/assets/icons';
import { FilterButton } from 'shared/components/ui/filter-button';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PageLoader } from 'shared/components/ui/page-loader';
import { FilterDocuments } from 'shared/constants';
import { useAllTasksCustomOrderByGrouping } from 'shared/hooks/use-all-tasks-custom-order-by-grouping';
import { useAllTasksOnReorder } from 'shared/hooks/use-all-tasks-on-reorder';
import { useAllTasksOnSectionCollapse } from 'shared/hooks/use-all-tasks-on-section-collapse';
import { useElectron } from 'shared/hooks/use-is-mac';
import { GroupByMode, TaskGroupBy } from 'shared/types/sorting-mode';
import { CalendarViewOptions } from 'shared/types/upcoming-view-options';
import { ProvidedTaskFilterList } from 'shared/views/task-filter-list';

import { useSelectedCalendarView } from '../../hooks/use-selected-calendar-view';
import * as Styled from './side-panel.style';

const supportedGroupByModes: TaskGroupBy[] = [
  GroupByMode.None,
  GroupByMode.Goal,
  GroupByMode.LifeArea,
  GroupByMode.Priority,
  GroupByMode.Type,
  GroupByMode.Deadline,
  GroupByMode.DeadlineDeadline,
  GroupByMode.Tag,
];

export type SidePanelProps = {
  onClose: () => void;
};

export const SidePanel: React.FC<SidePanelProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedView = useSelectedCalendarView();
  const { isMac } = useElectron();

  const goals = useSortedGoals();
  const tags = useSortedTags();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const filterDocument = useAllTasksFilterDocument();
  const customOrder = useAllTasksCustomOrderByGrouping(
    filterDocument.groupByMode,
  );
  const updateCustomOrder = useAllTasksOnReorder(filterDocument.groupByMode);
  const updateCollapsedSections = useAllTasksOnSectionCollapse();

  const updateFilterDocument = useUpdateAllTasksFilterDocument();

  const onReset = () =>
    updateFilterDocument(FilterDocuments.AllTasks.defaultFilterDocument);

  return (
    <Styled.Container>
      <Styled.Header $isElectronMac={isMac}>
        <Styled.Title>{t('pages.upcoming.all-tasks.title')}</Styled.Title>

        <Styled.HeaderInteractions>
          <FilterButton
            filterDocument={filterDocument}
            defaultFilterDocument={
              FilterDocuments.AllTasks.defaultFilterDocument
            }
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            updateFilterDocument={updateFilterDocument}
            supportedGroupByModes={supportedGroupByModes}
            onReset={onReset}
            preventDrag
          />
          <IconButton
            icon={XCircle}
            onClick={onClose}
            color={Colors.Default}
            preventDrag
          />
        </Styled.HeaderInteractions>
      </Styled.Header>

      <Styled.Content ref={scrollRef}>
        <Suspense fallback={<PageLoader />}>
          <ProvidedTaskFilterList
            filterDocument={filterDocument}
            customOrder={customOrder}
            onReorder={updateCustomOrder}
            onSectionCollapse={updateCollapsedSections}
            scrollElement={scrollRef}
            includeDuration={selectedView !== CalendarViewOptions.Month}
            denseFormat
          />
        </Suspense>
      </Styled.Content>
    </Styled.Container>
  );
};
